import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "../../../../../assets/stylesheets/editorPage.css";
import "../../../../../assets/stylesheets/homepage.css";
import { scrollPage, dispatchEvent } from "../../../../utis/helper";
import { useRecipientNotification } from "../../../../utis/useLaserLinkSettingsHook";
import { EditorInfoContext, UserInfoContext } from "../../../../utis/userInfoContext";
import { utilsGetLocalStorage } from "../../../../utis/utilsUpdateLocalStorage";
import apiGETRequest from "../../../../utis/apiRequest";
import DraggableComponent from "../../../CommonComponents/Draggable/DraggableComponent";
import ToastNotification from "../../../CommonComponents/Toasts/ToastNotification";
import { networkService } from "../../../config/axios";
import Element from "../../../Elements/KDFElements/Element";
import LaserLinkModal from "../../../Elements/LaserLink/LaserLinkModal";
import Button from "../../../Elements/Button";
import CommonModal from "../../../Modals/CommonModal";
import AddElementModal from "../../../Modals/EditorModals/AddElementModal";
import CloneElementModal from "../../../Modals/EditorModals/CloneElementModal";
import CookieConsentModal from "../../../Modals/EditorModals/CookieConsentModal";
import CookieInfoBanner from "../../../CommonComponents/CookieInfoBanner";
import DeleteElementModal from "../../../Modals/EditorModals/DeleteElementModal";
import LightBoxModal from "../../../Modals/EditorModals/LightBoxModal";
import EditorHeader from "./editorHeader/EditorHeader";
import SettingsEnableOPTIN from "./editorHeader/viewElements/SettingsEnableOPTIN";
import PdfSidebar from "./editorSideBar/PdfSidebar";
import EditorToolbox from "./editorToolbox/EditorToolbox";
import ToolBoxHeading from "./editorToolbox/toolBox/ToolBoxHeading";
import TermsModals from "../../../Modals/TermsModals";
import PasswordProtectedKDF from "./PasswordProtectedKDF";
import Viewer from "./pdfViewer/Viewer";
import { usePageFromHash } from "../../../../hooks/utils";
import useStatHook from "../../Stats/StatsHook/useStatHook";
import { getCookie } from "../../../../utis/helper";

let timedThrottleTimeout;

// Helper function to log Jump to Page elements
const logJumpToPageElements = (elementsData, kdfTitle, kdfId, isPublished) => {
  if (!elementsData || typeof elementsData !== 'object') return;

  const jumpToPageElements = [];

  // Iterate through all elements to find those with Jump to Page actions
  Object.values(elementsData).forEach(element => {
    if (!element) return;

    const hasJumpToPage = element.action === 'page' || element.value?.page_link;

    if (hasJumpToPage) {
      const elementInfo = {
        elementId: element.id,
        elementType: element.element_type_name || 'Unknown',
        onPage: element.page || element.geometry?.page || 'Unknown',
        jumpToPage: element.jump_to_page || element.value?.page_link || 'Unknown',
        reactivateScrolling: element.value?.reactivate_scroll || false
      };
      jumpToPageElements.push(elementInfo);
    }
  });
};

// --- Timed config resolver (handles flash_* or timed_* names) ---
const getTimedConfig = (tlb = {}) => {
  const enabled = (tlb.flash_pop ?? tlb.timed_pop) === true;
  const s = tlb.flash_pop_settings ?? tlb.timed_pop_settings ?? {};
  return {
    enabled,
    onPage: Number(s.on_page ?? s.page ?? 0),
    waitSecs: Number(s.seen_seconds ?? s.wait_seconds ?? 0),
    device: s.display_flash_device ?? s.display_device ?? "On all devices",
    lbId: s.lightbox ?? s.lightbox_id ?? null,
  };
};

//  Safely find the LB node for a given page + lbId across variants ---
const findTimedLbElements = (data, onPage, lbId) => {
  if (!data) return null;
  const pageData = data.page[onPage];
  let result = null;
  for (const [key, value] of Object.entries(pageData)) {
    if (value.id === Number(lbId)){
      result = value
    }
  }
  return result?.elements || null;
};


const EditKdfPage = ({ editortype, ...props }) => {
  if (props?.type == "password") {
    return <PasswordProtectedKDF alert={props?.alert} />;
  }
  const { pathname, hash } = useLocation();
  const redirectPage = usePageFromHash(hash)
  const { tlb, sess_id, session } = window?.objInit || {};
  const { pdfFile, pdfImages, doc, time_zone_list, currentUser } = props;

  const [openSideSection, setOpenSideSection] = useState(false);
  const [zoomIndex, setZoomIndex] = useState({
    index: 'page-height',
    value: "0.75",
  });
  const [docFiles, setDocFiles] = useState({});
  const [openCloneModal, setOpenCloneModal] = useState(false);
  const [laserLINKEditStatus, setLaserLINKEditStatus] = useState(null);
  const [isPdfPublished, setIsPdfPublished] = useState(false)
  const [isToolboxExpanded, setIsToolboxExpanded] = useState(false); // Toolbox collapsed by default

  // New state variables for controlling the background and alert visibility
  const [viewerBgClass, setViewerBgClass] = useState("");
  const [alertTextVisible, setAlertTextVisible] = useState(false);

  // edit page context states
  const [editEnabled, setEditEnabled] = useState(false);
  const [openAddElement, setOpenAddElement] = useState(false);
  const [toolType, setToolType] = useState("");
  const [rectangles, setRectangles] = useState([]);
  const [activeRectangleIndex, setActiveRectangleIndex] = useState(null);
  const [geo, setGeo] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    keep_proportions: true,
    keep_within_page: true,
    page: 1,
  });
  const [editElement, setEditElement] = useState({});
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [elements, setElements] = useState({});
  // Initialize to saved page so the PDF renders there; the refresh modal appears on top and
  // the user either stays (Yes) or navigates to landing page (No).
  const [pageNumber, setPageNumber] = useState(() => {
    if (editortype === "laserlink" && tlb?.token) {
      const saved = sessionStorage.getItem(`ll_page_${tlb.token}`);
      if (saved) {
        const savedPage = parseInt(saved, 10);
        if (savedPage > 0) return savedPage;
      }
    }
    return tlb?.landing_page || 1;
  });
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [savedRefreshPage, setSavedRefreshPage] = useState(null);
  const [lightBoxList, setLightBoxList] = useState([]);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [editLightBox, setEditLightBox] = useState({});
  const [imageElement, setImageElement] = useState({});
  const [selectedPdf, setSelectedPdf] = useState("");
  const [showToast, setShowToast] = useState({
    showToast: false,
    toastType: "success",
    toastMessage: "",
  });
  const [showLightBox, setShowLightBox] = useState(null);
  const [lightBoxListData, setLightBoxListData] = useState({});
  const [tagValue, setTagValue] = useState(doc?.show_tags_outside ?? true);
  const [pageDimensions, setPageDimensions] = useState({
    height: "",
    width: "",
  });
  const statHook = useStatHook(true) || {};
  const [isOptInPending, setIsOptInPending] = useState(false);
  const [isOptInBlockingActive, setIsOptInBlockingActive] = useState(false);
  const [isOptInModalOpen, setIsOptInModalOpen] = useState(false);
  const optInPollIntervalRef = useRef(null);
  const hasInitiallyLoadedRef = useRef(false); // Track if initial load completed

  const trackLightboxOpen = async (lightboxId, type) => {
  try {
    await statHook?.popupTrack?.(lightboxId, type);
  } catch (e) {
    console.error('[POP] popupTrack failed', e);
  }
};

  const blockerTimerStartedRef = useRef(false);

  const handleBlockerEvent = () => {
    if (tlb?.disable_scroll === true) {
      setViewerBgClass("scroll_disable");
      // Timer and text are started in startBlockerTimer() once the PDF finishes rendering
    }
  };

  const startBlockerTimer = () => {
    if (blockerTimerStartedRef.current) return;
    if (tlb?.disable_scroll !== true || tlb?.disable_settings?.disable_on === 'cyoa') return;
    blockerTimerStartedRef.current = true;
    setAlertTextVisible(true);
    setTimeout(() => {
      setViewerBgClass("");
      setAlertTextVisible(false);
    }, (tlb?.disable_settings?.disable_for || 0) * 1000);
  };

  const handleLightBoxEvent = async () => {
    let { data } = await networkService(
      `docs/all-user-lightboxes/all.json?tl_id=${tlb?.id}&by_trigger=yes&doc_id=${tlb?.doc?.id}&language=${tlb?.language_flag?.locale}`, 'GET'
    );
    setLightBoxListData(data);
  };


  const [enterDeviceLightBox, setEnterDeviceLightBox] = useState(false);
  const [exitDeviceLightBox, setExitDeviceLightBox] = useState(false);
  const [pageDeviceLightBox, setPageDeviceLightBox] = useState(false);
  const [isActionDisabled, setIsActionDisabled] = useState(doc?.disable_actions ?? true);

  // UserInfoContext states
  const [userData, setUserData] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [userCompanyData, setUserCompanyData] = useState(null);
  const [showCreateTerms, setShowCreateTerms] = useState(false);
  const [openPrivacyNotice, setOpenPrivacyNotice] = useState(false);
  const [showNotifications, setShowNotifications] = useState({
    showToast: false,
    toastType: "success",
    toastMessage: "",
  });
  const hasFetchedUserDataRef = useRef(false);
  // Track if component has mounted to prevent statHook from overriding initial pageNumber
  const hasInitializedPageRef = useRef(false);
  // Determine any refresh action SYNCHRONOUSLY during render (before any useEffect can
  // overwrite sessionStorage). The session-save effect fires on mount and would clobber
  // the saved page if we read it inside a useEffect instead.
  const refreshActionRef = useRef((() => {
    if (editortype !== "laserlink" || !tlb?.token) return null;

    const savedKey = `ll_page_${tlb.token}`;
    const saved = sessionStorage.getItem(savedKey);
    if (!saved) return null;

    const savedPage = parseInt(saved, 10);
    const landingPage = tlb?.landing_page || 1;

    // Check optin BEFORE the landingPage guard — the optin page can equal the landing page,
    // and we still need to navigate there and re-show the modal on every refresh until opted in.
    const optInCompleted = sessionStorage.getItem(`optin_completed_${tlb.token}`) === 'true';
    const hasOptIn = tlb?.has_optin === true;

    if (hasOptIn && !optInCompleted) {
      sessionStorage.removeItem(savedKey);
      // Signal SettingsEnableOPTIN to skip the delay and show modal immediately on this load.
      sessionStorage.setItem(`optin_refresh_${tlb.token}`, 'true');
      let optInPage;
      if (tlb?.optin_2nd_page_viewed) {
        optInPage = landingPage > 1 ? landingPage + 1 : 2;
      } else {
        optInPage = Number(tlb?.opt_in?.show_on_page) || landingPage;
      }
      return { type: 'optin', page: optInPage };
    }

    if (!savedPage || savedPage < 1 || savedPage === landingPage) {
      sessionStorage.removeItem(savedKey);
      return null;
    }

    return { type: 'modal', page: savedPage };
  })());

  const fetchUserData = useCallback(async () => {
    // Permanent guard: once set, never reset — prevents all duplicate calls
    if (hasFetchedUserDataRef.current) return;
    hasFetchedUserDataRef.current = true;

    try {
      let userDataResponse = await apiGETRequest(`edit.json`);
      if (userDataResponse && !userDataResponse.error) {
        setUserData(userDataResponse);
        let userProfileDataResponse = await apiGETRequest(
          `profiles/${userDataResponse?.id}.json`
        );
        if (userProfileDataResponse && !userProfileDataResponse.error) {
          setUserProfileData(userProfileDataResponse);
          if (userProfileDataResponse.privacy_link) {
            window.__userPrivacyLink = userProfileDataResponse.privacy_link;
          }
        }
      }
    } catch (_error) {
      // User data fetch failed (e.g. not logged in on laserlink); non-fatal
    }
  }, []);

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

useEffect(() => {
  const input = document.getElementById("pageNumber");
  if (input) {
    const val = Number(input.value);
    if (!Number.isNaN(val)) {
      setPageNumber(val);
    }
  }
}, [lightBoxListData]);


useEffect(() => {
  // CRITICAL FIX: Don't let statHook override the initial pageNumber on first mount
  // This was causing the landing page bug - statHook.pageNum was resetting to 1 after correct initialization
  if (!hasInitializedPageRef.current) {
    hasInitializedPageRef.current = true;
    return;
  }

  if (statHook?.pageNum) {
    setPageNumber(statHook.pageNum);
  }
}, [statHook?.pageNum]);

// CRITICAL: Keep #pageNumber input synchronized with pageNumber state AT ALL TIMES
// MUST use useLayoutEffect (not useEffect) to run synchronously BEFORE paint
// This ensures input value is set BEFORE PdfViewer reads it on initial render
useLayoutEffect(() => {
  const input = document.getElementById("pageNumber");
  if (input && pageNumber) {
    input.value = pageNumber;
  }
}, [pageNumber]);

// Save current page to sessionStorage (enables page restoration on refresh)
useEffect(() => {
  if (editortype === "laserlink" && tlb?.token && pageNumber) {
    if (window.__kdf_orientation_changing) return;
    sessionStorage.setItem(`ll_page_${tlb.token}`, pageNumber.toString());
  }
}, [pageNumber, tlb?.token, editortype]);


// Block all browser zoom when CYOA is enabled on LaserLink viewer
useEffect(() => {
  // Only apply zoom restrictions for LaserLink viewers with CYOA enabled
  const isCyoaEnabled = editortype === "laserlink" && tlb?.cyoa_enabled === true;

  if (!isCyoaEnabled) return;

  // Block keyboard zoom shortcuts (Ctrl/Cmd + Plus/Minus/0)
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')) {
      e.preventDefault();
      return false;
    }
  };

  // Block mouse wheel zoom (Ctrl/Cmd + wheel)
  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      return false;
    }
  };

  
  // Block pinch-to-zoom on touch devices
  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length > 1) {
      e.preventDefault();
      return false;
    }
  };

  // Block gesture events (iOS Safari)
  const handleGestureStart = (e) => {
    e.preventDefault();
    return false;
  };

  document.addEventListener('touchstart', handleTouchStart, { passive: false });
  document.addEventListener('gesturestart', handleGestureStart, { passive: false });
  document.addEventListener('keydown', handleKeyDown, { passive: false });
  document.addEventListener('wheel', handleWheel, { passive: false });


  // Add viewport meta tag to prevent zoom on mobile
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  const originalContent = viewportMeta ? viewportMeta.getAttribute('content') : null;

  if (viewportMeta) {
    viewportMeta.setAttribute('content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
    );
  }

  // Cleanup function
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('wheel', handleWheel);
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('gesturestart', handleGestureStart);

    // Restore original viewport meta tag
    if (viewportMeta && originalContent) {
      viewportMeta.setAttribute('content', originalContent);
    }
  };
}, [editortype, tlb?.cyoa_enabled]);

useEffect(() => {
  const input = document.getElementById("pageNumber");
  if (!input) return;

  const onChange = () => {
    const val = Number(input.value);
    if (!Number.isNaN(val)) {
      setPageNumber(val);
    }
  };

  onChange();
  input.addEventListener('change', onChange);
  return () => input.removeEventListener('change', onChange);
}, []);

useEffect(() => {
  if (Object.keys(lightBoxListData)?.length > 0) {
    const isMobile = window.innerWidth <= 768;
    const displayEnterDevice = tlb?.enter_pop_settings?.display_enter_device;
    const popupOptionsById = Object.values(lightBoxListData?.doc).reduce((acc, item) => {
      acc[`${tlb?.enter_pop_settings?.lightbox}`] = item;
      return acc;
    }, {});
    if (Number(pageNumber) === Number(tlb?.landing_page || 1)) {
      let lightBoxValueToUpdate =
        popupOptionsById[tlb?.enter_pop_settings?.lightbox]?.elements;

      if (displayEnterDevice === "On mobile" && isMobile) {
        setEnterDeviceLightBox(true);
        setShowLightBox(lightBoxValueToUpdate);
        trackLightboxOpen(tlb?.enter_pop_settings?.lightbox, 'enter');
      } else if (displayEnterDevice === "On Device" && !isMobile) {
        setEnterDeviceLightBox(true);
        setShowLightBox(lightBoxValueToUpdate);
        trackLightboxOpen(tlb?.enter_pop_settings?.lightbox, 'enter');
      } else if (displayEnterDevice === "On all devices") {
        setEnterDeviceLightBox(true);
        setShowLightBox(lightBoxValueToUpdate);
        trackLightboxOpen(tlb?.enter_pop_settings?.lightbox, 'enter');
      }
    } else {
    }
  }
}, [lightBoxListData, pageNumber]);   


  const handleMouseMove = (e) => {
    const mouseY = e.clientY;
    if (mouseY <= 10) {
      const isMobile = window.innerWidth <= 768;
      const displayEnterDevice = tlb?.exit_pop_settings?.display_device;
      const popupOptionsById = Object.values(lightBoxListData?.onexit).reduce((acc, item) => {
        acc[`${tlb?.exit_pop_settings?.lightbox}`] = item;
        return acc;
      }, {});

      if (displayEnterDevice === "On mobile" && isMobile) {
        setExitDeviceLightBox(true);
        let lightBoxValueToUpdate =
            popupOptionsById[tlb?.exit_pop_settings?.lightbox]?.elements;
        setShowLightBox(lightBoxValueToUpdate);
        trackLightboxOpen(tlb?.exit_pop_settings?.lightbox, 'exit')
      } else if (displayEnterDevice === "On Device" && !isMobile) {
        setExitDeviceLightBox(true);
        let lightBoxValueToUpdate =
            popupOptionsById[tlb?.exit_pop_settings?.lightbox]?.elements;
        setShowLightBox(lightBoxValueToUpdate);
        trackLightboxOpen(tlb?.exit_pop_settings?.lightbox, 'exit')
      } else if (displayEnterDevice === "On all devices") {
        setExitDeviceLightBox(true);
        let lightBoxValueToUpdate =
            popupOptionsById[tlb?.exit_pop_settings?.lightbox]?.elements;
        setShowLightBox(lightBoxValueToUpdate);
        trackLightboxOpen(tlb?.exit_pop_settings?.lightbox, 'exit')
      }
    }
  };

  useEffect(() => {
    if (
      tlb?.exit_pop === true &&
      enterDeviceLightBox === false &&
      exitDeviceLightBox === false &&
      Object.keys(lightBoxListData)?.length > 0
    ) {
      window.addEventListener("mousemove", handleMouseMove);
    }
    return () => {
      window?.removeEventListener("mousemove", handleMouseMove);
    };
  }, [lightBoxListData]);

useEffect(() => {
  const { enabled, onPage, waitSecs, device, lbId } = getTimedConfig(tlb);

  if (!enabled || !onPage || !lbId) return;
  if (!lightBoxListData || !Object.keys(lightBoxListData).length) return;
  if (pageNumber == null) return;

  if (Number(pageNumber) === Number(onPage)) {
    if (timedThrottleTimeout) clearTimeout(timedThrottleTimeout);
    timedThrottleTimeout = setTimeout(() => {
      const elementsNode = findTimedLbElements(lightBoxListData, onPage, lbId);
      if (elementsNode) {
        setPageDeviceLightBox(true);
        setShowLightBox(elementsNode);
        trackLightboxOpen(lbId, "timed");
      }
    }, Math.max(0, waitSecs) * 1000);

    return () => clearTimeout(timedThrottleTimeout);
  }
}, [pageNumber, lightBoxListData, tlb]);


useEffect(() => {
  const handleTimedPop = (e) => {
    const { lightboxId } = e.detail || {};
    const lbNode =
      lightBoxListData?.page?.[pageNumber]?.[lightboxId]?.elements ||
      lightBoxListData?.page?.[String(pageNumber)]?.[lightboxId]?.elements;

    if (lbNode) {
      setPageDeviceLightBox(true);
      setShowLightBox(lbNode);
    }
  };

  window.addEventListener("openTimedPop", handleTimedPop);
  return () => window.removeEventListener("openTimedPop", handleTimedPop);
}, [pageNumber, lightBoxListData]);


  const updateDocHeaderData = () => {
    document.title = doc?.title ?? "Kinetizine";
    document.head.innerHTML += `<title></title><meta name='description' content='${tlb?.seo_description}'/>`;
    let descMeta = document.querySelector("meta[name='description']");
    descMeta.setAttribute("content", tlb?.seo_description);
  };

  const onLoadSuccess = () => {
    if (editortype === "editor") return;
    const hashPage = new URLSearchParams(window.location.hash.replace("#", "")).get("page");
    if (hashPage) return;

    // Execute any pending refresh action (opt-in redirect or show continue/restart modal)
    if (refreshActionRef.current) {
      const action = refreshActionRef.current;
      refreshActionRef.current = null;
      if (action.type === 'optin') {
        handlePDFChangeAction(action.page);
      } else if (action.type === 'modal') {
        setSavedRefreshPage(action.page);
        setShowRefreshModal(true);
      }
      startBlockerTimer();
      return;
    }

    handlePDFChangeAction(tlb?.landing_page || 1);
    startBlockerTimer();
  };

  // Fetch fresh docFiles from server
  const refreshDocFiles = useCallback(async () => {
    if (!doc?.id) return;
    try {
      const { data, status } = await networkService(`docs/${doc.id}/files.json`, "GET");
      if (status === 200 && Array.isArray(data)) {
        setDocFiles(data);
      }
    } catch (error) {
      console.error("Error refreshing docFiles:", error);
    }
  }, [doc?.id]);

  // Landscape fullscreen feel: extend content into safe areas on laserlink viewer.
  // viewport-fit=cover allows the page to use the full screen (behind notch/home indicator).
  // Safari auto-collapses its bars on scroll, and 100dvh ensures the container adapts.
  useEffect(() => {
    if (editortype !== "laserlink") return;
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) return;

    const original = viewportMeta.getAttribute('content');
    if (!original.includes('viewport-fit=cover')) {
      viewportMeta.setAttribute('content', original + ', viewport-fit=cover');
    }

    return () => {
      if (original) viewportMeta.setAttribute('content', original);
    };
  }, [editortype]);

  useEffect(() => {
    setDocFiles(window?.objInit?.doc_files);
    setSelectedPdf(pdfFile);
    updateDocHeaderData();
    handleBlockerEvent();
    handleLightBoxEvent();

    // Restore page state if returning from preview
    // Only for editor type (not preview/viewer)
    if (editortype === "editor") {
      try {
        const storedState = sessionStorage.getItem('editorNavigationState');
        if (storedState) {
          const { returnPage } = JSON.parse(storedState);
          if (returnPage && !isNaN(returnPage)) {
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
              handlePDFChangeAction(Number(returnPage));
            }, 100);
          }
          // Clear the stored state immediately after reading it
          sessionStorage.removeItem('editorNavigationState');
        }
      } catch (error) {
        // If parsing fails or sessionStorage is unavailable, just continue normally
        console.warn('Failed to restore page state:', error);
      }
    }
  }, []);

  // Refresh docFiles when returning from navigation
  useEffect(() => {
    const handleFocus = () => {
      // Skip during orientation change — iOS fires focus on every rotation.
      // refreshDocFiles during rotation causes a re-render mid-recalc.
      if (window.__kdf_orientation_changing) return;
      refreshDocFiles();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Skip during orientation change — iOS fires visibilitychange on every rotation.
        if (window.__kdf_orientation_changing) return;
        refreshDocFiles();
      }
    };

    const handlePopState = () => {
      // Immediate refresh on browser back/forward
      refreshDocFiles();
    };

    const handlePageShow = (e) => {
      // This fires when page is loaded from cache (back/forward navigation)
      if (e.persisted) {
        refreshDocFiles();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [refreshDocFiles]);

  // Determine if Opt-In overlay should be shown (blocks interactions before Opt-In completion)
  useEffect(() => {
    const checkOptInStatus = () => {
      // During orientation change, page numbers are unreliable — skip page-based
      // checks but still detect if opt-in was already completed so the overlay
      // doesn't stay frozen.
      if (window.__kdf_orientation_changing) {
        const receiverCookie = getCookie("receiver", {});
        const isApiDone = utilsGetLocalStorage("receiver_api") === "true";
        const isReceiverDone = Object.keys(receiverCookie).length > 0 && receiverCookie[tlb?.token] !== undefined;
        const optinDone = getCookie("optin", {})[tlb?.token]?.show === "none";
        if (isApiDone || isReceiverDone || optinDone) {
          setIsOptInPending(false);
          setIsOptInBlockingActive(false);
          return false;
        }
        // Optin not completed yet — return current blocking state so polling
        // continues (keeps the interval alive to detect completion later).
        return isOptInBlockingActive;
      }

      if (editEnabled || concentModalStatus) {
        setIsOptInPending(false);
        setIsOptInBlockingActive(false);
        return false;
      }

      // Check if Opt-In is enabled
      if (!tlb?.has_optin) {
        setIsOptInPending(false);
        setIsOptInBlockingActive(false);
        return false;
      }

      // Check if Opt-In is already completed (read fresh values each time)
      const receiverCookie = getCookie("receiver", {});
      const optinCookie = getCookie("optin", {});
      const isApiCalled = utilsGetLocalStorage("receiver_api") === "true";
      
      const isReceiverSetUp = 
        Object.keys(receiverCookie).length > 0 &&
        receiverCookie[tlb?.token] !== undefined;
      
      // If Opt-In is already completed (receiver set up or API called), don't show overlay
      if (isApiCalled || isReceiverSetUp) {
        setIsOptInPending(false);
        setIsOptInBlockingActive(false);
        return false;
      }
      
      // Check Opt-In cookie status
      // If Opt-In was marked as "none" (completed), don't show overlay
      // If Opt-In modal is open ("display") OR not yet set (first visit), activate blocking immediately.
      // Previously we only activated when cookie was "display", leaving a gap while SettingsEnableOPTIN
      // ran its show_delay countdown. Fast scrollers could bypass the overlay in that window.
      const optInCookieStatus = optinCookie[tlb?.token]?.show;
      if (optInCookieStatus === "none") {
        setIsOptInPending(false);
        setIsOptInBlockingActive(false);
        return false;
      }
      // Opt-in not yet completed: activate blocking overlay right away regardless of whether
      // the cookie is "display" (modal already shown) or undefined (modal not shown yet).
      setIsOptInBlockingActive(true);

      // Check if current page matches Opt-In page conditions.
      // Use strict > (not >=) so the overlay only activates on pages BEYOND the opt-in
      // trigger page. On the trigger page itself the modal handles the presentation — showing
      // the overlay there too would blur the background before the modal appears, creating a
      // jarring double-block effect. The snap-back effect still works correctly because it only
      // fires when pageNumber > optInPage.
      const matchesPageCondition =
        (tlb?.optin_2nd_page_viewed &&
          (tlb?.landing_page > 1
            ? (pageNumber >= tlb?.landing_page + 1 || pageNumber <= tlb?.landing_page - 1)
            : pageNumber > 1)) ||
        (!tlb?.optin_2nd_page_viewed &&
          pageNumber > Number(tlb?.opt_in?.show_on_page));

      setIsOptInPending(matchesPageCondition);
      return matchesPageCondition;
    };

    // Clear any existing interval first
    if (optInPollIntervalRef.current) {
      clearInterval(optInPollIntervalRef.current);
      optInPollIntervalRef.current = null;
    }

    // Initial check
    const isPending = checkOptInStatus();

    // If Opt-In is pending, poll for completion every second
    if (isPending) {
      optInPollIntervalRef.current = setInterval(() => {
        const stillPending = checkOptInStatus();
        if (!stillPending && optInPollIntervalRef.current) {
          clearInterval(optInPollIntervalRef.current);
          optInPollIntervalRef.current = null;
        }
      }, 1000);
    }

    // Cleanup interval on unmount or dependency change
    return () => {
      if (optInPollIntervalRef.current) {
        clearInterval(optInPollIntervalRef.current);
        optInPollIntervalRef.current = null;
      }
    };
  }, [pageNumber, tlb?.has_optin, tlb?.opt_in?.show_on_page, tlb?.optin_2nd_page_viewed, tlb?.landing_page, editEnabled, concentModalStatus, tlb?.token]);

  // Scroll-back enforcement: if a visitor scrolls past the opt-in trigger page before
  // completing the opt-in (e.g. fast wheel scroll that advances multiple pages in one React
  // batch), immediately snap them back to the trigger page.
  // This is the third layer of defence — the overlay already blocks interaction, but without
  // this effect the visitor could briefly see content beyond the gate before React re-renders.
  useEffect(() => {
    if (window.__kdf_orientation_changing) return;
    if (!isOptInPending || !isOptInBlockingActive || editEnabled || concentModalStatus) {
      return;
    }
    if (!tlb?.has_optin) {
      return;
    }

    // CRITICAL FIX: Don't snap on initial page load (allow session storage restore)
    // Only snap on user navigation AFTER initial load
    // This prevents: User on page 4 → refresh (manual or rotation) → snap-back incorrectly navigates to opt-in page
    if (!hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
      return;
    }

    let snapToPage = null;

    if (tlb?.optin_2nd_page_viewed) {
      if (tlb?.landing_page > 1) {
        // Forward over-scroll: snap back to the first forward trigger page
        if (pageNumber > tlb?.landing_page + 1) {
          snapToPage = tlb?.landing_page + 1;
        // Backward over-scroll: snap back to the first backward trigger page
        } else if (pageNumber < tlb?.landing_page - 1) {
          snapToPage = tlb?.landing_page - 1;
        }
      } else {
        // Landing page is 1: opt-in fires on any page > 1. Snap back to page 2 if over-scrolled.
        if (pageNumber > 2) {
          snapToPage = 2;
        }
      }
    } else {
      const optInPage = Number(tlb?.opt_in?.show_on_page);
      if (optInPage > 0 && pageNumber > optInPage) {
        snapToPage = optInPage;
      }
    }

    if (snapToPage !== null) {
      scrollPage(snapToPage, "instant");
      const input = document.getElementById("pageNumber");
      if (input && Number(input.value) !== snapToPage) {
        input.value = String(snapToPage);
        dispatchEvent("change", "pageNumber");
      }
    }
  }, [pageNumber, isOptInPending, isOptInBlockingActive]);

  useRecipientNotification(
    editEnabled,
    pageNumber,
    tlb,
    `notify-engagement/${tlb?.token}/${sess_id}.json`
  );

  // Check if cookie consent already exists in DB session or localStorage
  // If cookie_status exists in session (from DB) or localStorage, user already made a choice - don't show modal again
  // This prevents showing the modal again on page refresh in normal browser (not incognito)
  const hasCookieConsentInDB = session && 
    session.cookie_status && 
    typeof session.cookie_status === 'string' && 
    session.cookie_status.trim() !== '';
  
  // Check localStorage - handle both "accepted_all" and "acceptAll" values
  const localStorageCookiesPrivacy = utilsGetLocalStorage("cookiesPrivacy");
  const hasCookieConsentInLocalStorage = localStorageCookiesPrivacy === "acceptAll" || 
    localStorageCookiesPrivacy === "accepted_all" ||
    localStorageCookiesPrivacy === "essential_only" ||
    localStorageCookiesPrivacy === "rejected";
  
  const [concentModalStatus, setConcentModalStatus] = useState(
    editEnabled == false &&
    tlb?.privacy_policy_consent == true &&
    !hasCookieConsentInDB && // Don't show if already saved in DB (user already made a choice)
    !hasCookieConsentInLocalStorage // Don't show if already in localStorage
  );

  // Ref to track if modal is being opened programmatically (via "Change cookie settings" link)
  const isProgrammaticOpenRef = useRef(false);

  // Update modal status when session data becomes available or changes
  // This ensures the modal is hidden if cookie consent exists in localStorage or DB
  // BUT only on initial load, not when user explicitly requests to change settings
  useEffect(() => {
    // Skip auto-hiding if this is a programmatic open (user clicked "Change cookie settings")
    if (isProgrammaticOpenRef.current) {
      isProgrammaticOpenRef.current = false; // Reset flag after one check
      return;
    }

    const currentSession = window?.objInit?.session;
    const currentHasCookieConsentInDB = currentSession && 
      currentSession.cookie_status && 
      typeof currentSession.cookie_status === 'string' && 
      currentSession.cookie_status.trim() !== '';
    
    const currentLocalStorageCookiesPrivacy = utilsGetLocalStorage("cookiesPrivacy");
    const currentHasCookieConsentInLocalStorage = currentLocalStorageCookiesPrivacy === "acceptAll" || 
      currentLocalStorageCookiesPrivacy === "accepted_all" ||
      currentLocalStorageCookiesPrivacy === "essential_only" ||
      currentLocalStorageCookiesPrivacy === "rejected";
    
    // If cookie consent exists in DB or localStorage, hide the modal
    // But only if this is not a user-initiated open
    if ((currentHasCookieConsentInDB || currentHasCookieConsentInLocalStorage) && concentModalStatus) {
      setConcentModalStatus(false);
    }
  }, [session, concentModalStatus]);

  // Listen for requests to open cookie consent modal (from CookieBlockedMessage)
  useEffect(() => {
    const handleOpenCookieModal = () => {
      // Only open modal if we're in viewer mode (not editor) and privacy notice consent is enabled
      if (editEnabled == false && tlb?.privacy_policy_consent == true) {
        isProgrammaticOpenRef.current = true; // Mark as programmatic open
        setConcentModalStatus(true);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('openCookieConsentModal', handleOpenCookieModal);
      return () => {
        window.removeEventListener('openCookieConsentModal', handleOpenCookieModal);
      };
    }
  }, [editEnabled, tlb?.privacy_policy_consent]);

  // Disable Right Click Event
  document.addEventListener("contextmenu", function (e) {
    e.preventDefault();
  });

  // Disable KeyBoard Shortcuts Event
  document.onkeydown = function (e) {
    if (event.keyCode == 123) {
      return false;
    }
    if (e.ctrlKey && e.shiftKey && e.keyCode == "I".charCodeAt(0)) {
      return false;
    }
    if (e.ctrlKey && e.shiftKey && e.keyCode == "J".charCodeAt(0)) {
      return false;
    }
    if (e.ctrlKey && e.keyCode == "U".charCodeAt(0)) {
      return false;
    }
  };

  const handleRefreshContinue = () => {
    setShowRefreshModal(false);
  };

  const handleRefreshRestart = () => {
    setShowRefreshModal(false);
    sessionStorage.removeItem(`ll_page_${tlb?.token}`);
    handlePDFChangeAction(tlb?.landing_page || 1);
  };

  const handlePDFChangeAction = (value) => {
    const target = Number(value);
    if (!target || Number.isNaN(target)) {
      return;
    }
    scrollPage(value, "instant");
    setPageNumber(value);
    setLoaclPage(value)
    try {
      statHook?.trackPageChange?.(target);
    } catch (e) {
      // non-fatal
    }
    const input = document.getElementById("pageNumber");
    if (input) {
      input.value = target;
      dispatchEvent("change", "pageNumber");
    } else {
      // non-fatal
    }
  };

  useEffect(() => {
    const fetchAllElements = async () => {
      const response = await networkService(
        `docs/all-elements/${selectedPdf?.id}.json`,
        "GET"
      );
      setElements(response?.data);

      // Console log for Jump to Page feature tracking
      if (response?.data && editortype === 'editor') {
        logJumpToPageElements(response.data, doc?.title, doc?.id, doc?.count_live_files > 0);
      }
    };
    if (selectedPdf?.id && editortype !== 'laserlink') {
      fetchAllElements();
    } else {
      const initialElements = window?.objInit?.elements;
      setElements(initialElements);

      // Console log for Jump to Page feature tracking on initial load
      if (initialElements && editortype === 'editor') {
        logJumpToPageElements(initialElements, doc?.title, doc?.id, doc?.count_live_files > 0);
      }
    }
  }, [selectedPdf]);

  useEffect(() => {
    if (redirectPage) {
      handlePDFChangeAction(redirectPage);
    }
  }, [redirectPage])
  const [loaclPage,setLoaclPage] = useState(pageNumber)

  return (
    <UserInfoContext.Provider
      value={{
        userInfoData: userData,
        userProfileData: userProfileData,
        userCompanyData: userCompanyData,
        showNotifications: showNotifications,
        showCreateTerms: showCreateTerms,
        setShowCreateTerms,
        setUserData,
        setUserProfileData,
        setUserCompanyData,
        setShowNotifications,
        openPrivacyNotice,
        setOpenPrivacyNotice
      }}
    >
      <EditorInfoContext.Provider
        value={{
          editEnabled: editEnabled,
          openAddElement: openAddElement,
          rectangles: rectangles,
          activeRectangleIndex: activeRectangleIndex,
          geo: geo,
          toolType,
          elements: elements,
          editElement: editElement,
          pageNumber: pageNumber,
          openRemoveModal,
          editLightBox,
          lightBoxList,
          imageElement,
          openLightBox,
          selectedPdf,
          showToast,
          tagValue,
          pageDimensions,
          setPageDimensions,
          setTagValue,
          setShowToast,
          setSelectedPdf,
          setOpenLightBox,
          setImageElement,
          setLightBoxList,
          setEditElement,
          setRectangles,
          setOpenAddElement,
          setEditEnabled,
          setActiveRectangleIndex,
          setGeo,
          setToolType,
          setElements,
          setOpenRemoveModal,
          setPageNumber,
          setEditLightBox,
          isActionDisabled,        
          setIsActionDisabled,   
        }}
      >
      {editEnabled == false &&
        concentModalStatus == false &&
        tlb?.has_optin == true ? (
        <SettingsEnableOPTIN
          pageNumber={pageNumber}
          tlb={tlb}
          sess_id={sess_id}
          actionCableOptIn={statHook?.actionCableOptIn}
          onModalOpenChange={setIsOptInModalOpen}
        />
      ) : (
        ""
      )}
      {showRefreshModal && (
        <CommonModal
          title=""
          modalStatus={showRefreshModal}
          changeActiveStatus={() => {}}
          centered
          size="sm"
          classList="alert-modal"
          closeModalOnEsc={false}
          footer={
            <div className="d-flex m-auto">
              <Button
                text="Restart"
                classList="alert"
                addonClass="my-2 mx-2 delete-btn"
                onClick={handleRefreshRestart}
              />
              <Button
                text="Resume"
                classList="success"
                addonClass="my-2 mx-2 delete-btn common-modal-yes-button"
                onClick={handleRefreshContinue}
              />
            </div>
          }
        >
          <p style={{ fontSize: "14px" }}>Looks like you left off on page {savedRefreshPage}. Would you like to resume from there?</p>
        </CommonModal>
      )}
      {/* Opt-In Interaction Blocking Overlay */}
      {(isOptInPending || isOptInModalOpen) && isOptInBlockingActive && editEnabled == false && concentModalStatus == false && (
        <div className="optin-blocking-overlay" />
      )}
      {concentModalStatus ? (
        <>
          <div
            className={`viewer-bg ${viewerBgClass} ${tlb?.cyoa_enabled == true ? "scroll_disable hidden-for-cyoa" : ""
              }`}
          >
            {/* editor page header */}
            <div className={tlb?.cyoa_enabled == true ? "cyoa-header-hidden" : ""}>
              <EditorHeader
                pdfFile={pdfFile}
                setOpenSideSection={setOpenSideSection}
                openSideSection={openSideSection}
                setZoomIndex={setZoomIndex}
                zoomIndex={zoomIndex}
                pageNumber={pageNumber}
                setPageNumber={(value) => handlePDFChangeAction(value)}
                docFiles={docFiles}
                setSelectedPdf={setSelectedPdf}
                title={doc?.title}
                downloadStatus={tlb?.enable_download ?? false}
                selectedPdf={selectedPdf}
                tlb={tlb ?? {}}
                editorStatus={
                  editortype == "editor" || pathname?.includes("viewer")
                }
                setLaserLINKEditStatus={setLaserLINKEditStatus}
                showToolbar={tlb?.show_toolbar ?? true}
                showEditBtn={editortype == "publish"}
                editortype={editortype}
                currentUserId={currentUser?.id}
                isPdfPublished={isPdfPublished}
                setIsPdfPublished={setIsPdfPublished}
                showThumbnail={tlb?.show_thumbnail ?? true}
                otherLanguageAvailable={docFiles?.length > 1}
              />

              {/* Informational banner about cookie-dependent content - editor only */}
              {editortype === "editor" && (
                <CookieInfoBanner
                  elements={elements}
                  docId={doc?.id}
                />
              )}
            </div>
            </div>

          <CookieConsentModal
          concentModalStatus={concentModalStatus}
          setConcentModalStatus={setConcentModalStatus}
          customizationText={tlb?.customization_text}
          customizationLink={tlb?.link_details}
          companyPrivacyUrl={tlb?.company_privay_url}
          sessId={sess_id}
          modalDetails={tlb?.modal_details}
          buttonStyles={tlb?.values}
        />
        
        </>
      ) : (
        <>
          <CommonModal
            modalStatus={showLightBox != null}
            changeActiveStatus={async (status) => {
              setShowLightBox(null);
              setExitDeviceLightBox(false);
              setEnterDeviceLightBox(false);
               setPageDeviceLightBox(false); 
            }}
            lightBox={true}
          >
            {!!showLightBox ? <Element elements={showLightBox ?? {}}  onClick={()=>{alert('test start')}}/> : ""}
          </CommonModal>
          <ToastNotification
            showToast={showToast}
            setShowToast={setShowToast}
          />

          {openRemoveModal && (
            <DeleteElementModal
              openDeleteModal={openRemoveModal}
              closeModal={setOpenRemoveModal}
              modalType={
                toolType == "LightboxesToolbox" &&
                  !editElement?.id &&
                  editLightBox?.id
                  ? "lightbox"
                  : "elemement"
              }
              elemId={
                toolType == "LightboxesToolbox" &&
                  !editElement?.id &&
                  editLightBox?.id
                  ? editLightBox?.id
                  : editElement?.id
              }
            />
          )}
          {/* addElementModal */}
          {openAddElement && (
            <AddElementModal
              openAddElementModal={openAddElement}
              closeModal={setOpenAddElement}
              docData={selectedPdf}
              docFiles={docFiles}
              totalPages={pdfFile?.num_pages}
              applyDesiredAction={statHook?.applyDesiredAction}
              zoom={zoomIndex}
            />
          )}
          {/* lightBoxSection */}
          {openLightBox && (
            <LightBoxModal
              openLightboxModal={openLightBox}
              docFileId={selectedPdf?.id}
              docId={doc?.id}
              setOpenLightBox={setOpenLightBox}
            />
          )}
          {/* clone elements in other language modal */}
          {openCloneModal && (
            <CloneElementModal
              openCloneModal={openCloneModal}
              closeModal={setOpenCloneModal}
              docFiles={docFiles}
              sourceId={selectedPdf?.id ?? pdfFile?.id}
            />
          )}
          <TermsModals
            openTerms={openPrivacyNotice}
            setOpenTerms={setOpenPrivacyNotice}
            refetch={() => {}}
          />
          {laserLINKEditStatus && (
            <LaserLinkModal
              laserItem={laserLINKEditStatus}
              time_zone_list={time_zone_list}
              docData={doc}
              triggerCancelProcess={setLaserLINKEditStatus}
              currentUser={currentUser}
              language={pdfFile?.language}
              thumbnail={pdfImages[0]}
            />
          )}
          <div
            className={`viewer-bg ${viewerBgClass} ${tlb?.cyoa_enabled == true ? "scroll_disable hidden-for-cyoa" : ""
              }`}
          >
            {/* editor page header */}
            <div className={tlb?.cyoa_enabled == true ? "cyoa-header-hidden" : ""}>
              <EditorHeader
                pdfFile={pdfFile}
                setOpenSideSection={setOpenSideSection}
                openSideSection={openSideSection}
                setZoomIndex={setZoomIndex}
                zoomIndex={zoomIndex}
                pageNumber={pageNumber}
                setPageNumber={(value) => handlePDFChangeAction(value)}
                docFiles={docFiles}
                setSelectedPdf={setSelectedPdf}
                title={doc?.title}
                downloadStatus={tlb?.enable_download ?? false}
                selectedPdf={selectedPdf}
                tlb={tlb ?? {}}
                editorStatus={
                  editortype == "editor" || pathname?.includes("viewer")
                }
                setLaserLINKEditStatus={setLaserLINKEditStatus}
                showToolbar={tlb?.show_toolbar ?? true}
                showEditBtn={editortype == "publish"}
                editortype={editortype}
                currentUserId={currentUser?.id}
                isPdfPublished={isPdfPublished}
                setIsPdfPublished={setIsPdfPublished}
                showThumbnail={tlb?.show_thumbnail ?? true}
                otherLanguageAvailable={docFiles?.length > 1}
              />
            </div>
            
            
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div
                className={`${tlb && tlb?.show_toolbar == false ? "" : "editor-content"
                  } ${window.self != window.top ? "insideIframe" : ""} ${tlb?.enable_shadow_box ? "withShadow" : ""
                  }`}
                style={{ width: '100vw', flex: 1, minHeight: 0 }}
              >
                {/* sidebar section */}
                {openSideSection && (
                  <PdfSidebar
                    pdfImages={pdfImages}
                    pageNumber={pageNumber}
                  />
                )}
                {/* handle the pdf view component */}
                {pdfFile && (
                  <Viewer
                    zoom={zoomIndex}
                    url={`${window.location.origin.toString()}${selectedPdf
                      ? selectedPdf?.url ?? selectedPdf?.file?.url
                      : pdfFile?.file?.url
                      }`}
                    docFileId={selectedPdf?.id}
                    docId={doc?.id}
                    onLoadSuccess={onLoadSuccess}
                    statHook={statHook}
                    editortype={editortype}
                    docFiles={docFiles}
                    timerOnDoc={
                      editortype != "editor" ? false : doc?.timer_on_doc
                    }
                    showToolbar={tlb?.show_toolbar ?? true}
                    setZoomIndex={setZoomIndex}
                    tlb={tlb}
                    pageNum={pageNumber}
                  />
                )}
              </div>
              {/* handled the edit toolbox */}
              {editortype === "editor" && (
                <DraggableComponent
                  header={<ToolBoxHeading isExpanded={isToolboxExpanded} setIsExpanded={setIsToolboxExpanded} />}
                  className={isToolboxExpanded ? 'expanded' : 'collapsed'}
                >
                  <EditorToolbox
                    doc={doc}
                    pdfFile={pdfFile}
                    otherLanguageAvailable={docFiles?.length > 1}
                    setOpenCloneModal={setOpenCloneModal}
                    selectedPdf={selectedPdf}
                    isPdfPublished={isPdfPublished}
                    setIsPdfPublished={setIsPdfPublished}
                    isExpanded={isToolboxExpanded}
                  />
                </DraggableComponent>
              )}
            </div>
            {alertTextVisible && tlb?.disable_settings.disable_on !== 'cyoa' && (
              <p className="pdf-alert-text">
                {tlb?.disable_settings?.disable_message}{" "}
                {tlb?.disable_settings?.disable_for}
              </p>
            )}
          </div>
        </>
      )}
      </EditorInfoContext.Provider>
    </UserInfoContext.Provider>
  );
};

export default EditKdfPage;
